import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {
  FiHome, FiCheckSquare, FiBriefcase, FiUsers, FiUserCheck,
  FiBarChart3, FiClock, FiCalendar, FiSettings
} = FiIcons;

const Sidebar = ({ isOpen }) => {
  const location = useLocation();
  const { user, hasPermission } = useAuth();
  const { settings } = useData();

  const menuItems = [
    { name: 'Dashboard', icon: FiHome, path: '/', permission: null },
    { name: 'Projects', icon: FiBriefcase, path: '/projects', permission: null },
    { name: 'Tasks', icon: FiCheckSquare, path: '/tasks', permission: null },
    { name: 'Time Tracking', icon: FiClock, path: '/time-tracking', permission: null },
    { name: 'Leaves', icon: FiCalendar, path: '/leaves', permission: null },
    { name: 'Team', icon: FiUsers, path: '/team', permission: 'view_team' },
    { name: 'Clients', icon: FiUserCheck, path: '/clients', permission: null },
    { name: 'Reports', icon: FiBarChart3, path: '/reports', permission: 'view_reports' },
    { name: 'Settings', icon: FiSettings, path: '/settings', permission: 'all' }
  ];

  const filteredItems = menuItems.filter(item =>
    !item.permission || hasPermission(item.permission) || hasPermission('all')
  );

  return (
    <motion.aside
      initial={false}
      animate={{ width: isOpen ? 256 : 64 }}
      transition={{ duration: 0.3 }}
      className="fixed left-0 top-0 h-full bg-white shadow-lg z-30 border-r border-gray-200"
    >
      <div className="p-4">
        <div className="flex items-center space-x-3">
          {settings?.branding?.logoUrl ? (
             <img src={settings.branding.logoUrl} alt="Logo" className="w-10 h-10 object-contain rounded" />
          ) : (
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-lg">
                {settings?.branding?.appName?.charAt(0) || 'C'}
              </span>
            </div>
          )}
          
          {isOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col overflow-hidden"
            >
              <span className="font-bold text-gray-900 truncate">
                {settings?.branding?.appName || 'CRM Pro'}
              </span>
              <span className="text-xs text-gray-500 truncate">
                Management System
              </span>
            </motion.div>
          )}
        </div>
      </div>

      <nav className="mt-8">
        {filteredItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center space-x-3 px-4 py-3 mx-2 rounded-lg transition-all ${
                isActive
                  ? 'bg-blue-50 text-blue-600 border-r-2 border-blue-600'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <SafeIcon
                icon={item.icon}
                className={`w-5 h-5 ${isActive ? 'text-blue-600' : 'text-gray-500'}`}
              />
              {isOpen && (
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="font-medium whitespace-nowrap"
                >
                  {item.name}
                </motion.span>
              )}
            </Link>
          );
        })}
      </nav>

      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute bottom-4 left-4 right-4"
        >
          <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg p-4 text-white">
            <div className="flex items-center space-x-3">
              <img
                src={user?.avatar}
                alt={user?.name}
                className="w-10 h-10 rounded-full"
              />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{user?.name}</p>
                <p className="text-xs opacity-75 capitalize">
                  {user?.role?.replace('_', ' ')}
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </motion.aside>
  );
};

export default Sidebar;